import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getApiUrl, getAuthHeaders } from "../config/api";

function TabPanel({ title, items, setItems, onCreate, onUpdate, onDelete }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState("");

  const filtered = items.filter(i => i.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="bg-white rounded-lg shadow p-3 sm:p-4 overflow-x-hidden">
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <input value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder={`Search ${title.toLowerCase()}`} className="border rounded px-3 py-2 w-full" />
        <input value={name} onChange={(e)=>setName(e.target.value)} placeholder={`New ${title} name`} className="border rounded px-3 py-2 w-full" />
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <input value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Description (optional)" className="border rounded px-3 py-2 flex-1 w-full" />
          <button onClick={async ()=>{ if(!name.trim()) return; const created = await onCreate({ name, description }); setItems([created, ...items]); setName(""); setDescription(""); }} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded w-full sm:w-auto shrink-0">Add</button>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {filtered.map((it, idx) => (
          <div key={it.id} className="border rounded-lg p-3">
            <div className="text-sm font-medium mb-1">Name</div>
            <input value={it.name} onChange={(e)=>{ const v=e.target.value; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, name: v }: x)); }} className="w-full border rounded px-2 py-1 mb-2" />
            <div className="text-sm font-medium mb-1">Description</div>
            <input value={it.description || ""} onChange={(e)=>{ const v=e.target.value; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, description: v }: x)); }} className="w-full border rounded px-2 py-1 mb-2" />
            <label className="flex items-center gap-2 text-sm mb-3"><input className="w-4 h-4" type="checkbox" checked={it.is_active !== false} onChange={(e)=>{ const v=e.target.checked; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, is_active: v }: x)); }} /> Active</label>
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <button onClick={async ()=>{ const updated = await onUpdate(it); setItems(prev=> prev.map((x)=> x.id===updated.id? updated: x)); }} className="px-3 py-2 border rounded text-sm w-full sm:w-auto">Save</button>
              <button onClick={async ()=>{ if(!window.confirm('Deactivate?')) return; await onDelete(it.id); setItems(prev=> prev.map(x=> x.id===it.id? {...x, is_active: false}: x)); }} className="px-3 py-2 border rounded text-sm text-red-600 w-full sm:w-auto">Deactivate</button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600 border-b">
              <th className="py-2 pr-2">Name</th>
              <th className="py-2 pr-2">Description</th>
              <th className="py-2 pr-2">Active</th>
              <th className="py-2 pr-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it, idx) => (
              <tr key={it.id} className="border-b">
                <td className="py-2 pr-2"><input value={it.name} onChange={(e)=>{ const v=e.target.value; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, name: v }: x)); }} className="w-full border rounded px-2 py-1" /></td>
                <td className="py-2 pr-2"><input value={it.description || ""} onChange={(e)=>{ const v=e.target.value; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, description: v }: x)); }} className="w-full border rounded px-2 py-1" /></td>
                <td className="py-2 pr-2"><input type="checkbox" checked={it.is_active !== false} onChange={(e)=>{ const v=e.target.checked; setItems(prev=> prev.map((x,i)=> i===idx? { ...x, is_active: v }: x)); }} /></td>
                <td className="py-2 pr-2 text-right">
                  <button onClick={async ()=>{ const updated = await onUpdate(it); setItems(prev=> prev.map((x)=> x.id===updated.id? updated: x)); }} className="px-2 py-1 border rounded mr-2">Save</button>
                  <button onClick={async ()=>{ if(!window.confirm('Deactivate?')) return; await onDelete(it.id); setItems(prev=> prev.map(x=> x.id===it.id? {...x, is_active: false}: x)); }} className="px-2 py-1 border rounded text-red-600">Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SuppliesLookups() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [v,b,c] = await Promise.all([
        axios.get(getApiUrl('/api/supplies/lookups/vendors'), { headers: getAuthHeaders() }),
        axios.get(getApiUrl('/api/supplies/lookups/brands'), { headers: getAuthHeaders() }),
        axios.get(getApiUrl('/api/supplies/lookups/categories'), { headers: getAuthHeaders() })
      ]);
      setVendors(v.data || []);
      setBrands(b.data || []);
      setCategories(c.data || []);
    };
    load();
  }, []);

  const api = {
    createVendor: (payload) => axios.post(getApiUrl('/api/supplies/lookups/vendors'), payload, { headers: getAuthHeaders() }).then(r=>r.data),
    updateVendor: (it) => axios.put(getApiUrl(`/api/supplies/lookups/vendors/${it.id}`), it, { headers: getAuthHeaders() }).then(r=>r.data),
    deleteVendor: (id) => axios.delete(getApiUrl(`/api/supplies/lookups/vendors/${id}`), { headers: getAuthHeaders() }).then(r=>r.data),

    createBrand: (payload) => axios.post(getApiUrl('/api/supplies/lookups/brands'), payload, { headers: getAuthHeaders() }).then(r=>r.data),
    updateBrand: (it) => axios.put(getApiUrl(`/api/supplies/lookups/brands/${it.id}`), it, { headers: getAuthHeaders() }).then(r=>r.data),
    deleteBrand: (id) => axios.delete(getApiUrl(`/api/supplies/lookups/brands/${id}`), { headers: getAuthHeaders() }).then(r=>r.data),

    createCategory: (payload) => axios.post(getApiUrl('/api/supplies/lookups/categories'), payload, { headers: getAuthHeaders() }).then(r=>r.data),
    updateCategory: (it) => axios.put(getApiUrl(`/api/supplies/lookups/categories/${it.id}`), it, { headers: getAuthHeaders() }).then(r=>r.data),
    deleteCategory: (id) => axios.delete(getApiUrl(`/api/supplies/lookups/categories/${id}`), { headers: getAuthHeaders() }).then(r=>r.data),
  };

  return (
      <div className="p-4 sm:p-6 overflow-x-hidden">
        <div className="mb-3 sm:mb-4 flex items-center justify-between">
          <button onClick={() => navigate('/supplies')} className="inline-flex items-center px-3 py-2 border rounded hover:bg-gray-50 text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Supplies
          </button>
          <div className="rounded-md shadow-sm flex flex-wrap gap-2 sm:gap-0" role="group">
            <button className={`px-3 py-2 text-sm border ${tab==='vendors'?'bg-blue-600 text-white':'bg-white'} w-full sm:w-auto`} onClick={()=>setTab('vendors')}>Vendors</button>
            <button className={`px-3 py-2 text-sm border sm:-ml-px ${tab==='brands'?'bg-blue-600 text-white':'bg-white'} w-full sm:w-auto`} onClick={()=>setTab('brands')}>Brands</button>
            <button className={`px-3 py-2 text-sm border sm:-ml-px ${tab==='categories'?'bg-blue-600 text-white':'bg-white'} w-full sm:w-auto`} onClick={()=>setTab('categories')}>Categories</button>
          </div>
        </div>

        {tab==='vendors' && (
          <TabPanel title="Vendors" items={vendors} setItems={setVendors} onCreate={api.createVendor} onUpdate={api.updateVendor} onDelete={api.deleteVendor} />
        )}
        {tab==='brands' && (
          <TabPanel title="Brands" items={brands} setItems={setBrands} onCreate={api.createBrand} onUpdate={api.updateBrand} onDelete={api.deleteBrand} />
        )}
        {tab==='categories' && (
          <TabPanel title="Categories" items={categories} setItems={setCategories} onCreate={api.createCategory} onUpdate={api.updateCategory} onDelete={api.deleteCategory} />
        )}
      </div>
  );
}


